import { isDefined } from '@sgme/fp';
import type { IntlFormatters } from 'react-intl';

import { isNotDefined } from '@/components/sg-design-system/utils/index.ts';
import type { AppState } from '@/store/store.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';
import { targetAccumulatorApi } from '@/store/targetAccumulator/api/index.ts';
import { createTargetAccumulatorRfsRequestParams } from '@/store/targetAccumulator/api/multiPass/requestTargetAccumulatorQuote.ts';
import {
  getTargetAccumulatorQuote,
  getTargetAccumulatorRfsId,
  getTargetAccumulatorRfsPremiumData,
  getTargetAccumulatorRfsQuoteId,
} from '@/store/targetAccumulator/selectors.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';
import { selectTileClientId, selectTilesOfTab } from '@/store/tiles/selectors.ts';
import { formatStringDateTime } from '@/utils/date.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

export const cancelAllTargetAccumulatorRfsThunk = createAppAsyncThunk<void, void>(
  'global/cancelRfs',
  async (_, api) => {
    const state: AppState = api.getState();
    const currentTabId = selectActiveTabId(state);
    const tiles = selectTilesOfTab(state, currentTabId);

    const cancellationActions = tiles
      .map(({ tileId }) => {
        const targetAccumulator = targetAccumulatorSlice.selectors.getTargetAccumulator(state, tileId);
        const rfsId = isDefined(targetAccumulator) ? getTargetAccumulatorRfsId(targetAccumulator) : undefined;
        if (isDefined(targetAccumulator) && isDefined(rfsId)) {
          return targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.initiate({
            tileId: tileId,
            rfsId: rfsId,
            removeQuote: true,
          });
        }
        return undefined;
      })
      .filter(isDefined);
    await Promise.all(cancellationActions.map((cancelAction) => api.dispatch(cancelAction)));
  },
);

interface TargetAccumulatorRfsThunkParams {
  tileId: string;
  forceTradeable: boolean;
  skipCreditCheck?: boolean;
}

export const requestTargetAccumulatorRfsThunk = createAppAsyncThunk<void, TargetAccumulatorRfsThunkParams>(
  'targetAccumulator/requestRfs',
  async (params, { dispatch, getState }) => {
    const { tileId, forceTradeable, skipCreditCheck = false } = params;

    let targetAccumulator = getState().targetAccumulator[tileId];
    if (isNotDefined(targetAccumulator)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    let rfsId = getTargetAccumulatorRfsId(targetAccumulator);

    if (isDefined(rfsId)) {
      await dispatch(
        targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.initiate({
          tileId,
          rfsId,
        }),
      );
    }

    targetAccumulator = getState().targetAccumulator[tileId];
    if (isNotDefined(targetAccumulator)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    rfsId = getTargetAccumulatorRfsId(targetAccumulator);
    const newRfsId = isDefined(rfsId) ? rfsId : getNewRfsId();
    const clientId = selectTileClientId(getState(), tileId);
    const signalRConnectionId = getSignalRConnectionId(getState());

    const rfsRequestParams = createTargetAccumulatorRfsRequestParams(
      targetAccumulator,
      tileId,
      newRfsId,
      clientId,
      signalRConnectionId,
      forceTradeable,
      skipCreditCheck,
    );

    if (isDefined(rfsRequestParams)) {
      await dispatch(
        targetAccumulatorApi.endpoints.requestTargetAccumulatorRfs.initiate(rfsRequestParams, {
          fixedCacheKey: newRfsId,
        }),
      );
    }
  },
);

export const resetTargetAccumulatorThunk = createAppAsyncThunk<void, { tileId: string; currencyPair?: string }>(
  'target-accumulator/reset',
  async ({ tileId, currencyPair }, { dispatch, getState }) => {
    const targetAccumulatorTile = targetAccumulatorSlice.selectors.getTargetAccumulator(getState(), tileId);

    if (isNotDefined(targetAccumulatorTile)) {
      return;
    }

    const rfsId = getTargetAccumulatorRfsId(targetAccumulatorTile);

    dispatch(targetAccumulatorSlice.actions.targetAccumulatorReset({ tileId }));
    if (isDefined(rfsId)) {
      dispatch(targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.initiate({ tileId, rfsId }));
    }

    await dispatch(
      targetAccumulatorApi.endpoints.checkTargetAccumulator.initiate({
        tileId: tileId,
        properties: {
          currencyPair: currencyPair ?? targetAccumulatorTile.properties.currencyPair,
        },
      }),
    );
  },
);

export const executeTargetAccumulatorThunk = createAppAsyncThunk<void, { tileId: string; userEmail: string }>(
  'target-accumulator/execute',
  async ({ tileId, userEmail }, { dispatch, getState }) => {
    const targetAccumulatorTile = targetAccumulatorSlice.selectors.getTargetAccumulator(getState(), tileId);
    const currentState = getState();

    if (isNotDefined(targetAccumulatorTile)) {
      return;
    }

    const preferedEmails = currentState.userPreferences.splitNotifications
      ? currentState.userPreferences.splitNotificationsEmailsOptions
      : currentState.userPreferences.emails;

    const notificationEmail = preferedEmails.length > 0 ? preferedEmails.join(';') : userEmail;

    const {
      notional,
      notionalCurrencyIndex,
      expiryDate,
      expiryDateTenor,
      firstFixingDate,
      currencyPair,
      strike,
      target,
    } = targetAccumulatorTile.properties;
    const clientId = selectTileClientId(currentState, tileId);

    const rfsId = getTargetAccumulatorRfsId(targetAccumulatorTile);
    const quote = getTargetAccumulatorQuote(targetAccumulatorTile);
    const quoteId = getTargetAccumulatorRfsQuoteId(targetAccumulatorTile);
    const [currency1, currency2] = isDefined(currencyPair) ? currencyPair.split('/') : ['', ''];

    const premiumData = getTargetAccumulatorRfsPremiumData(quote);
    const premiumAmount = premiumData?.premiumAmount;

    const premiumAmountString = isDefined(premiumAmount)
      ? premiumAmount[targetAccumulatorTile.properties.premiumTypeCurrencyIndex - 1]
      : undefined;

    if (
      isNotDefined(userEmail) ||
      isNotDefined(clientId) ||
      isNotDefined(quoteId) ||
      isNotDefined(rfsId) ||
      isNotDefined(currency1) ||
      isNotDefined(currency2) ||
      isNotDefined(notional) ||
      isNotDefined(notionalCurrencyIndex) ||
      isNotDefined(strike) ||
      isNotDefined(target) ||
      isNotDefined(expiryDate) ||
      isNotDefined(firstFixingDate) ||
      isNotDefined(quote)
    ) {
      return;
    }

    dispatch(
      targetAccumulatorApi.endpoints.executeTargetAccumulatorRfs.initiate(
        {
          notificationEmail: notificationEmail,
          tileId,
          quoteId,
          rfsId,
          accountId: clientId,
          currency1,
          currency2,
          notional,
          notionalCurrencyIndex,
          userLocalDateTime: formatStringDateTime(new Date()),
          strike: strike,
          target: target,
          expiry: {
            date: expiryDate,
            tenor: expiryDateTenor,
          },

          firstFixing: {
            date: firstFixingDate,
            tenor: targetAccumulatorTile.properties.firstFixingTenor,
          },
          clientsWay: targetAccumulatorTile.properties.clientsWay,
          fixingFrequency: targetAccumulatorTile.properties.fixingFrequency,
          numberOfFixings: targetAccumulatorTile.properties.numberOfFixings,
          premium: String(premiumAmountString),
          accuType: targetAccumulatorTile.properties.accuType ?? 'TargetAccu',
          premiumCurrencyIndex: targetAccumulatorTile.properties.premiumTypeCurrencyIndex,
          amountMode: targetAccumulatorTile.properties.amountMode,
          lastQuoteReceivedByFrontTimestamp: quote.frontTimestamp,
          lastQuoteFromBackTimestamp: quote.backTimestamp,
        },
        { fixedCacheKey: rfsId },
      ),
    );
  },
);

export const copyToClipBoardTargetAccumulatorThunk = createAppAsyncThunk<
  void,
  { tileId: string; formatNumber: IntlFormatters['formatNumber'] }
>('target-accumulator/copyToClipboard', async ({ tileId, formatNumber }, { getState }) => {
  const formatNumberWithDecimal = (value: number) =>
    formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const targetAccumulatorTile = targetAccumulatorSlice.selectors.getTargetAccumulator(getState(), tileId);
  if (isNotDefined(targetAccumulatorTile)) {
    return;
  }
  const accumulatorQuoteData = getTargetAccumulatorQuote(targetAccumulatorTile);

  const {
    amountMode: amountModeFromStore,
    currencyPair,
    clientsWay,
    fixingFrequency,
    fixingReference1,
    leverage,
    leverageAmount,
    firstFixingDate,
    firstFixingTenor,
    hedgeType,
    expiryDate,
    expiryDateTenor,
    strike,
    premiumTypeCurrencyIndex,
    numberOfFixings,
    notional,
    notionalCurrencyIndex,
    accuType,
    targetProfitType,
    target,
    strikeDown,
    pivot,
    strikeUp,
    ekiDown,
    ekiUp,
    ekiTrigger,
  } = targetAccumulatorTile?.properties || {};

  let premium: number | undefined;
  let premiumPercent: number | undefined;
  const currency = currencyPair?.split('/')[notionalCurrencyIndex - 1];

  if (accumulatorQuoteData) {
    const { premiumCcy1, premiumCcy2, premiumPctCcy1, premiumPctCcy2 } = accumulatorQuoteData.premiumData;
    premium = [premiumCcy1, premiumCcy2][premiumTypeCurrencyIndex - 1]?.ask;
    premiumPercent = [premiumPctCcy1, premiumPctCcy2][premiumTypeCurrencyIndex - 1];
  }

  const underlyingField = `Underlying: ${currencyPair} (${clientsWay} ${currency})\n`;
  const fixingFrequencyField = `Fixing Frequency: ${numberOfFixings} ${fixingFrequency} (${fixingReference1})\n`;
  const settlementFrequencyField = `Settlement Frequency: ${numberOfFixings} ${fixingFrequency}\n`;
  const leverageField = `Leverage: ${leverage}\n`;

  const amountMode = amountModeFromStore === 'Total' || firstFixingDate === expiryDate ? 'Total Amount' : 'Per Fixing';
  const notionalField = `Notional: ${formatNumberWithDecimal(Number(notional))} ${currency} (${amountMode})\n`;
  const leveragedNotionalField = `Leveraged Notional: ${formatNumberWithDecimal(Number(leverageAmount))} ${currency}\n`;

  const firstFixingField = `First Fixing: ${firstFixingDate} ${firstFixingTenor ? `(${firstFixingTenor})` : ''}\n`;
  const lastDateField =
    expiryDate !== null ? `Last Date: ${expiryDate} ${expiryDateTenor ? `(${expiryDateTenor})` : ''}\n` : '';
  const strikeField = strike !== null ? `Strike: ${strike}\n` : '';

  const hedge = accumulatorQuoteData?.hedge;

  const spotRef = hedgeType === 'Spot' ? `Spot Ref: ${accumulatorQuoteData ? `${hedge?.rateMid}` : ''}\n` : '';

  const clientPaysOrReceives = premium ? (premium >= 0 ? 'pays' : 'receives') : '';

  const clientPremium = accumulatorQuoteData
    ? `Client ${clientPaysOrReceives} ${formatNumberWithDecimal(Math.abs(Number(premium)))} ${currency} (${premiumPercent}%)`
    : '';
  const premiumField = clientPremium ? `Premium: ${clientPremium}` : '';

  const accuTypeVariant = accuType !== 'TargetAccu' ? accuType : '';
  const ekiValueField = accuType === 'EKI' ? `EKI Trigger: ${ekiTrigger}\n` : '';

  const accuTypeData =
    accuType === 'PIVOT'
      ? `Strike down: ${strikeDown}\n` + `Pivot: ${pivot}\n` + `Strike up: ${strikeUp}\n`
      : `EKI down: ${ekiDown}\n` +
        `Strike down: ${strikeDown}\n` +
        `Pivot: ${pivot}\n` +
        `Strike up: ${strikeUp}\n` +
        `EKI up: ${ekiUp}\n`;

  const summary =
    `Product: TARF ${accuTypeVariant}\n` +
    `${underlyingField}` +
    `${fixingFrequencyField}` +
    `${settlementFrequencyField}` +
    `${leverageField}` +
    `${notionalField}` +
    `${leveragedNotionalField}` +
    `${firstFixingField}` +
    `${lastDateField}` +
    `${spotRef}` +
    `${accuTypeData}` +
    `${ekiValueField}` +
    `${strikeField}` +
    `Profit Target: ${target} (${targetProfitType ?? ''})\n` +
    `${premiumField}`;

  await navigator.clipboard.writeText(summary);
});
